import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useCustomerAuth } from "../CustomerAuthContext/CustomerAuthContext";
import axiosInstance from "../../api/axiosInstance";

/* ─── Types ─── */
export type FavoriteItem = { favoriId: number; urunId: number };

type FavoriteCtx = {
  favorites: FavoriteItem[];
  loading: boolean;
  isFavorite: (urunId: number) => boolean;
  toggle: (urunId: number) => Promise<void>;
  reload: () => Promise<void>;
};

/* ─── Context ─── */
const Ctx = createContext<FavoriteCtx>({
  favorites: [],
  loading: false,
  isFavorite: () => false,
  toggle: async () => {},
  reload: async () => {},
});

export const useFavorites = () => useContext(Ctx);

/* ─── Provider ─── */
export const FavoriteProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { customer, token } = useCustomerAuth();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(false);

  const musteriId = customer?.id;

  const reload = useCallback(async () => {
    if (!musteriId || !token) {
      setFavorites([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axiosInstance.post("/Favoriler/listele", { musteriId });
      if (res.data?.success && Array.isArray(res.data.data)) {
        setFavorites(
          res.data.data.map((x: any) => ({
            favoriId: x.favoriId,
            urunId: x.urunId,
          }))
        );
      }
    } catch {
      /* yoksay */
    } finally {
      setLoading(false);
    }
  }, [musteriId, token]);

  // Giriş/çıkışta yenile
  useEffect(() => {
    reload();
  }, [reload]);

  const isFavorite = useCallback(
    (urunId: number) => favorites.some((f) => f.urunId === urunId),
    [favorites]
  );

  const toggle = useCallback(
    async (urunId: number) => {
      if (!musteriId || !token) return;
      const alreadyFav = favorites.some((f) => f.urunId === urunId);

      // Optimistic update
      setFavorites((prev) =>
        alreadyFav
          ? prev.filter((f) => f.urunId !== urunId)
          : [...prev, { favoriId: -1, urunId }]
      );

      try {
        const endpoint = alreadyFav ? "/Favoriler/sil" : "/Favoriler/ekle";
        await axiosInstance.post(endpoint, { musteriId, urunId });
        if (!alreadyFav) await reload(); // gerçek favoriId'yi al
      } catch {
        await reload(); // hata durumunda geri al
      }
    },
    [musteriId, token, favorites, reload]
  );

  return (
    <Ctx.Provider value={{ favorites, loading, isFavorite, toggle, reload }}>
      {children}
    </Ctx.Provider>
  );
};
